import React, { useState, useEffect } from 'react';
import { MdAdd, MdEdit, MdDelete, MdSwapHoriz, MdPhoneAndroid, MdSignalWifiOff, MdSignalWifi4Bar } from 'react-icons/md';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../../config/api';

const DevicesView = ({ 
  data, 
  searchTerm, 
  onSearchChange, 
  onRefresh,
  user 
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch users for assignment (field officers only)
  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/users?userRole=MMDA&mmda_id=${user.mmda_id}`,
        API_CONFIG
      );
      // Filter to show only field officers for device assignment
      const fieldOfficers = response.data.filter(user =>
        user.role_name && user.role_name.toLowerCase().includes('field')
      );
      setUsers(fieldOfficers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [user.mmda_id]);

  const handleReassignDevice = (device) => {
    setSelectedDevice(device);
    setShowReassignModal(true);
  };

  const handleAddDevice = () => {
    setShowAddModal(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return '#4caf50';
      case 'assigned': return '#2196f3';
      case 'inactive': return '#f44336';
      case 'maintenance': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return <MdSignalWifi4Bar />;
      case 'assigned': return <MdPhoneAndroid />;
      case 'inactive': return <MdSignalWifiOff />;
      default: return <MdPhoneAndroid />;
    }
  };

  const filteredData = data.filter(device =>
    device.device_serial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.assigned_user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#333', fontSize: '24px', fontWeight: '600' }}>
            Device Management
          </h2>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
            Manage and assign devices to field officers
          </p>
        </div>
        <button
          onClick={handleAddDevice}
          style={{
            backgroundColor: '#2196f3',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#1976d2'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#2196f3'}
        >
          <MdAdd size={18} />
          Add Device
        </button>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search devices by serial, officer, or status..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '12px 16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none'
          }}
        />
      </div>

      {/* Devices Table */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>
                Device Serial
              </th>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>
                Status
              </th>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>
                Assigned Field Officer
              </th>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>
                Last Sync
              </th>
              <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#333' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: '#666',
                  fontSize: '16px'
                }}>
                  {searchTerm ? 'No devices match your search' : 'No devices found'}
                </td>
              </tr>
            ) : (
              filteredData.map((device) => (
                <tr 
                  key={device.device_id}
                  style={{ 
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: getStatusColor(device.status),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        {getStatusIcon(device.status)}
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', color: '#333' }}>
                          {device.device_serial}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          ID: {device.device_id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: getStatusColor(device.status) + '20',
                      color: getStatusColor(device.status)
                    }}>
                      {device.status || 'Unknown'}
                    </span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: '#333' }}>
                        {device.assigned_user_name || 'Unassigned'}
                      </div>
                      {device.assigned_to && device.assigned_user_role && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {device.assigned_user_role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} (ID: {device.assigned_to})
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {device.last_sync_at ? 
                        new Date(device.last_sync_at).toLocaleDateString() : 
                        'Never'
                      }
                    </div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleReassignDevice(device)}
                      style={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#4caf50'}
                    >
                      <MdSwapHoriz size={14} />
                      Update
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Device Modal */}
      {showAddModal && (
        <AddDeviceModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onRefresh={onRefresh}
          user={user}
          users={users}
        />
      )}

      {/* Reassign Device Modal */}
      {showReassignModal && selectedDevice && (
        <ReassignDeviceModal
          isOpen={showReassignModal}
          onClose={() => {
            setShowReassignModal(false);
            setSelectedDevice(null);
          }}
          device={selectedDevice}
          users={users}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
};

// Add Device Modal Component
const AddDeviceModal = ({ isOpen, onClose, onRefresh, user, users }) => {
  const [formData, setFormData] = useState({
    device_serial: '',
    assigned_to: '',
    status: 'assigned'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/devices`, {
        ...formData,
        mmda_id: user.mmda_id,
        assigned_to: formData.assigned_to || null
      }, API_CONFIG);

      onRefresh();
      onClose();
      alert('Device added successfully!');
    } catch (error) {
      console.error('Error adding device:', error);
      alert('Failed to add device. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>
          Add New Device
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Device Serial *
            </label>
            <input
              type="text"
              value={formData.device_serial}
              onChange={(e) => setFormData({ ...formData, device_serial: e.target.value })}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Assign to Field Officer
            </label>
            <select
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Select Field Officer (Optional)</option>
              {users.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.full_name} - {user.phone}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="assigned">Assigned</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                backgroundColor: 'white',
                cursor: 'pointer',
                color: 'black'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#2196f3',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Adding...' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Reassign Device Modal Component
const ReassignDeviceModal = ({ isOpen, onClose, device, users, onRefresh }) => {
  const [selectedUser, setSelectedUser] = useState(device.assigned_to || '');
  const [selectedStatus, setSelectedStatus] = useState(device.status || 'assigned');
  const [loading, setLoading] = useState(false);

  const handleReassign = async () => {
    setLoading(true);

    try {
      await axios.put(`${API_BASE_URL}/devices/${device.device_id}`, {
        ...device,
        assigned_to: selectedUser || null,
        status: selectedStatus
      }, API_CONFIG);

      onRefresh();
      onClose();
      alert('Device updated successfully!');
    } catch (error) {
      console.error('Error updating device:', error);
      alert('Failed to update device. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>
          Update Device Assignment & Status
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 16px 0', color: '#666' }}>
            Device: <strong>{device.device_serial}</strong>
          </p>
          <p style={{ margin: '0 0 16px 0', color: '#666' }}>
            Currently assigned to: <strong>{device.assigned_user_name || 'Unassigned'}</strong>
          </p>

          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
            Reassign to Field Officer
          </label>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="">Unassign Device</option>
            {users.map(user => (
              <option key={user.user_id} value={user.user_id}>
                {user.full_name} - {user.phone}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
            Device Status
          </label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="assigned">Assigned - Device is assigned but not yet active</option>
            <option value="active">Active - Device is working and in use</option>
            <option value="inactive">Inactive - Device is not working or disabled</option>
            <option value="maintenance">Maintenance - Device is being serviced</option>
          </select>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            Current status: <strong style={{ textTransform: 'capitalize' }}>{device.status || 'Unknown'}</strong>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer',
              color:'black'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleReassign}
            disabled={loading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#4caf50',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Updating...' : 'Update Device'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DevicesView;
